import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Building2, Factory, Home } from "lucide-react";
import { CompanyData } from "../../../models/company-model";
import { TimeZonesData } from "../../../models/time-zones";
import MapPicker from "../components/MapPicker";
import RequiredMark from "../components/RequiredMark";
import SettingToggleRow from "../components/SettingToggleRow";
import { PROPERTY_TYPES, PropertyDetails, SETTING_SECTIONS } from "../types";
import { passtrackColors } from "../theme";
import { geocodeAddress } from "../utils/geocode";
import { lookupMexicanPostalCode } from "../utils/postalCode";

const PROPERTY_TYPE_ICONS: Record<string, typeof Home> = {
  home: Home,
  factory: Factory,
  "building-2": Building2,
};

interface PropertyStepProps {
  companies: CompanyData[];
  companiesLoading: boolean;
  selectedCompany: CompanyData | null;
  onSelectCompany: (company: CompanyData | null) => void;
  newCompanyName: string;
  onNewCompanyNameChange: (value: string) => void;
  prop: PropertyDetails;
  onUpdateProp: (patch: Partial<PropertyDetails>) => void;
  onToggleSetting: (key: keyof PropertyDetails["settings"], checked: boolean) => void;
  timeZones: TimeZonesData[];
  timeZonesLoading: boolean;
}

const CardShell = ({ children }: { children: React.ReactNode }) => (
  <Card variant="outlined" sx={{ p: 3.5, borderRadius: 2.5 }}>
    {children}
  </Card>
);

const formatCoordinate = (value: string) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed.toFixed(5) : value;
};

export default function PropertyStep({
  companies,
  companiesLoading,
  selectedCompany,
  onSelectCompany,
  newCompanyName,
  onNewCompanyNameChange,
  prop,
  onUpdateProp,
  onToggleSetting,
  timeZones,
  timeZonesLoading,
}: PropertyStepProps) {
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const onUpdatePropRef = useRef(onUpdateProp);
  onUpdatePropRef.current = onUpdateProp;
  const [colonias, setColonias] = useState<string[]>([]);
  const [postalLookupLoading, setPostalLookupLoading] = useState(false);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const geocodeRequestIdRef = useRef(0);

  useEffect(() => {
    const zip = (prop.zip ?? "").trim();
    if (!/^\d{5}$/.test(zip)) {
      setColonias([]);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setPostalLookupLoading(true);
      try {
        const result = await lookupMexicanPostalCode(zip);
        if (cancelled) {
          return;
        }
        if (!result) {
          setColonias([]);
          return;
        }

        setColonias(result.colonias);
        onUpdatePropRef.current({
          state: result.state,
          city: result.city,
          colonia: "",
        });
      } finally {
        if (!cancelled) {
          setPostalLookupLoading(false);
        }
      }
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [prop.zip]);

  useEffect(() => {
    const street = (prop.street ?? "").trim();
    const streetNumber = (prop.streetNumber ?? "").trim();
    const colonia = (prop.colonia ?? "").trim();
    const city = (prop.city ?? "").trim();
    const state = (prop.state ?? "").trim();
    const zip = (prop.zip ?? "").trim();

    const requestId = ++geocodeRequestIdRef.current;
    const timer = window.setTimeout(async () => {
      setGeocodeLoading(true);
      try {
        const result = await geocodeAddress({
          street,
          streetNumber,
          colonia,
          city,
          state,
          zip,
        });

        if (requestId !== geocodeRequestIdRef.current) {
          return;
        }
        if (!result) {
          return;
        }

        onUpdatePropRef.current({
          lat: result.lat.toFixed(5),
          lng: result.lng.toFixed(5),
        });
      } finally {
        if (requestId === geocodeRequestIdRef.current) {
          setGeocodeLoading(false);
        }
      }
    }, 700);

    return () => window.clearTimeout(timer);
  }, [
    prop.street,
    prop.streetNumber,
    prop.colonia,
    prop.city,
    prop.state,
    prop.zip,
  ]);

  const onLogoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onUpdateProp({ logo: String(reader.result ?? "") });
    reader.readAsDataURL(file);
  };

  const settingsTotal = SETTING_SECTIONS.reduce(
    (acc, section) => acc + section.items.length,
    0,
  );
  const settingsActive = SETTING_SECTIONS.reduce(
    (acc, section) =>
      acc + section.items.filter((item) => prop.settings[item.key]).length,
    0,
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1fr) 320px" },
        gap: 3,
        alignItems: "start",
        maxWidth: 1180,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <CardShell>
          <Typography sx={{ mb: 2 }} variant="subtitle2" color="text.secondary">
            Selecciona compañía o ingresa una nueva
            <RequiredMark />
          </Typography>
          <Autocomplete
            value={selectedCompany}
            onChange={(_, value) => onSelectCompany(value)}
            options={companies}
            getOptionLabel={(option) => option.name ?? ""}
            loading={companiesLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Compañía"
                placeholder="Buscar compañía existente"
                size="small"
                required={!newCompanyName.trim()}
              />
            )}
            renderOption={(liProps, option) => (
              <li {...liProps} key={option.id}>
                {option.name ?? ""}
              </li>
            )}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Nueva compañía (nombre)"
            placeholder="Escribe el nombre si das de alta una nueva"
            value={newCompanyName}
            onChange={(e) => onNewCompanyNameChange(e.target.value)}
            required={!selectedCompany}
          />
        </CardShell>

        <CardShell>
          <Typography sx={{ mb: 2 }} fontWeight={600} color={passtrackColors.ink}>
            Tipo de propiedad
            <RequiredMark />
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 1.75 }}>
            {PROPERTY_TYPES.map((t) => {
              const on = prop.type === t.value;
              const Icon = PROPERTY_TYPE_ICONS[t.icon] ?? Home;
              return (
                <Box
                  key={t.value}
                  component="button"
                  onClick={() => onUpdateProp({ type: t.value })}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    alignItems: "flex-start",
                    textAlign: "left",
                    p: 2,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    background: on ? passtrackColors.cloud : "#fff",
                    border: `1.5px solid ${on ? passtrackColors.blue : passtrackColors.borderSubtle}`,
                    font: "inherit",
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.25,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: on ? passtrackColors.blue : passtrackColors.cloud,
                      color: on ? "#fff" : passtrackColors.blue,
                    }}
                  >
                    <Icon size={18} strokeWidth={2} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 15, color: passtrackColors.ink }}>
                    {t.label}
                  </Typography>
                  <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: passtrackColors.textMuted }}>
                    {t.description}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </CardShell>

        <CardShell>
          <Typography sx={{ mb: 2 }} fontWeight={600} color={passtrackColors.ink}>
            Identificación
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "112px minmax(0,1fr)", gap: 2, alignItems: "center", mb: 2 }}>
            <Box
              component="button"
              onClick={() => logoInputRef.current?.click()}
              sx={{
                width: 112,
                height: 112,
                borderRadius: 1.5,
                border: `1.5px dashed ${prop.logo ? passtrackColors.borderDefault : passtrackColors.blue200}`,
                background: prop.logo ? "#fff" : passtrackColors.mist,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                cursor: "pointer",
                overflow: "hidden",
                p: 0,
                backgroundImage: prop.logo ? `url(${prop.logo})` : "none",
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              {!prop.logo && (
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: passtrackColors.blue900 }}>
                  Subir logo
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <TextField
                fullWidth
                size="small"
                label="Nombre de la propiedad"
                value={prop.name}
                onChange={(e) => onUpdateProp({ name: e.target.value })}
                placeholder="Ej. Residencial Altavista"
                required
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontSize: 12, color: passtrackColors.textMuted }}>
                  PNG o SVG, fondo transparente. Máx. 2 MB.
                </Typography>
                {prop.logo && (
                  <Box
                    component="button"
                    onClick={() => onUpdateProp({ logo: "" })}
                    sx={{ background: "none", border: "none", color: passtrackColors.red, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                  >
                    Quitar
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <input
            type="file"
            accept="image/*"
            ref={logoInputRef}
            onChange={onLogoFile}
            style={{ display: "none" }}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Código postal"
              value={prop.zip}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, "").slice(0, 5);
                onUpdateProp({ zip: next });
              }}
              placeholder="11000"
              inputProps={{ inputMode: "numeric", maxLength: 5 }}
              helperText={postalLookupLoading ? "Buscando ubicación..." : undefined}
            />
            <TextField
              fullWidth
              size="small"
              label="Estado"
              value={prop.state ?? ""}
              onChange={(e) => onUpdateProp({ state: e.target.value })}
              placeholder="Ciudad de México"
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Ciudad"
              value={prop.city ?? ""}
              onChange={(e) => onUpdateProp({ city: e.target.value })}
              placeholder="Ciudad de México"
            />
            <FormControl fullWidth size="small">
              <InputLabel id="property-colonia-label">Colonia</InputLabel>
              <Select
                labelId="property-colonia-label"
                label="Colonia"
                value={prop.colonia ?? ""}
                onChange={(e) => onUpdateProp({ colonia: e.target.value })}
                disabled={colonias.length === 0}
              >
                {colonias.map((colonia) => (
                  <MenuItem key={colonia} value={colonia}>
                    {colonia}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)", gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Calle"
              value={prop.street ?? ""}
              onChange={(e) => onUpdateProp({ street: e.target.value })}
              placeholder="Av. Paseo de la Reforma"
            />
            <TextField
              fullWidth
              size="small"
              label="Número exterior"
              value={prop.streetNumber ?? ""}
              onChange={(e) => onUpdateProp({ streetNumber: e.target.value })}
              placeholder="2210"
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr)", gap: 2 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel id="property-tz-label">Zona horaria</InputLabel>
              <Select
                labelId="property-tz-label"
                label="Zona horaria"
                value={prop.tz ?? ""}
                onChange={(e) => onUpdateProp({ tz: e.target.value })}
                disabled={timeZonesLoading}
              >
                {timeZones.map((tz) => (
                  <MenuItem key={tz.id} value={String(tz.id)}>
                    {tz.time_zone ?? ""}
                    {tz.utc_offset != null
                      ? ` (UTC${tz.utc_offset >= 0 ? "+" : ""}${tz.utc_offset})`
                      : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${passtrackColors.borderSubtle}` }}>
            <Box sx={{ mb: 2 }}>
              <Typography fontWeight={600} color={passtrackColors.ink}>
                Ubicación en el mapa
              </Typography>
              <Typography sx={{ fontSize: 13, color: passtrackColors.textMuted }}>
                {geocodeLoading
                  ? "Actualizando ubicación a partir de la dirección..."
                  : "Revisa la ubicación en el mapa y ajusta el pin o las coordenadas si hace falta."}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Latitud"
                value={prop.lat}
                onChange={(e) => onUpdateProp({ lat: e.target.value })}
                onBlur={() =>
                  onUpdateProp({ lat: formatCoordinate(prop.lat) })
                }
                placeholder="19.42604"
                inputProps={{ inputMode: "decimal" }}
              />
              <TextField
                fullWidth
                size="small"
                label="Longitud"
                value={prop.lng}
                onChange={(e) => onUpdateProp({ lng: e.target.value })}
                onBlur={() =>
                  onUpdateProp({ lng: formatCoordinate(prop.lng) })
                }
                placeholder="-99.17153"
                inputProps={{ inputMode: "decimal" }}
              />
            </Box>
            <MapPicker
              lat={prop.lat}
              lng={prop.lng}
              onChange={(lat, lng) => onUpdateProp({ lat, lng })}
            />
          </Box>
        </CardShell>

        <CardShell>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2, gap: 2 }}>
            <Box>
              <Typography fontWeight={600} color={passtrackColors.ink}>
                Configuración de la propiedad
              </Typography>
              <Typography sx={{ fontSize: 13, color: passtrackColors.textMuted }}>
                Funciones y permisos que estarán disponibles en esta propiedad. Podrás cambiarlos después.
              </Typography>
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                background: passtrackColors.cloud,
                color: passtrackColors.blue,
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {settingsActive} de {settingsTotal} activas
            </Box>
          </Box>
          {SETTING_SECTIONS.map((section) => (
            <Box key={section.title} sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: passtrackColors.blue500,
                  mb: 1,
                }}
              >
                {section.title}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {section.items.map((entry) => (
                  <SettingToggleRow
                    key={entry.key}
                    entry={entry}
                    checked={prop.settings[entry.key]}
                    onChange={(checked) => onToggleSetting(entry.key, checked)}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </CardShell>
      </Box>

      <Card
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 2.5,
          borderTop: "4px solid transparent",
          backgroundImage: `linear-gradient(#fff,#fff), ${passtrackColors.blue}`,
          backgroundOrigin: "border-box",
          backgroundClip: "padding-box, border-box",
        }}
      >
        <Typography fontWeight={600} sx={{ mb: 1.5 }} color={passtrackColors.ink}>
          Qué configuraremos
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[
            "Puntos de acceso, con sus puertas en pares entrada / salida.",
            "Grupos de acceso que cruzan puntos con los cuatro roles de la plataforma.",
            "Requisitos fotográficos y campos personalizados del registro.",
            "Unidades de la propiedad, empezando por Administración.",
            "Usuario administrador e invitaciones iniciales por rol.",
          ].map((text) => (
            <Typography key={text} sx={{ fontSize: 13, lineHeight: 1.5, color: passtrackColors.graphite }}>
              • {text}
            </Typography>
          ))}
        </Box>
        <Typography sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${passtrackColors.borderSubtle}`, fontSize: 12, color: passtrackColors.textMuted }}>
          La unidad de <strong style={{ color: passtrackColors.ink }}>Administración</strong> y su usuario administrador se crean siempre en este alta inicial.
        </Typography>
      </Card>
    </Box>
  );
}
